import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import type { FC, ReactNode } from 'react';
import { EDR_COLORS } from './edrTokens';

const useDrawerStyles = makeStyles(() => ({
  drawerBackdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.4) !important',
    backdropFilter: 'blur(5px)',
    transition:
      'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 300ms cubic-bezier(0.4, 0, 0.2, 1) !important',
  },
  drawerPaper: {
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) !important',
  },
}));

type EdrDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  subtitleMono?: boolean;
  headerIcon?: ReactNode;
  topBadges?: ReactNode;
  children: ReactNode;
  width?: number;
};

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <Box style={{ padding: '12px 16px', borderTop: `1px solid ${EDR_COLORS.borderSubtle}` }}>
    <Typography
      style={{
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: EDR_COLORS.textMuted,
        marginBottom: 6,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const DetailRow: FC<{ label: string; value: ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="flex-start"
    style={{ gap: 10, padding: '4px 0' }}
  >
    <Typography style={{ fontSize: 12, color: EDR_COLORS.textSecondary, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      style={{
        fontSize: 12,
        color: EDR_COLORS.textPrimary,
        fontWeight: 500,
        textAlign: 'right',
        fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
        wordBreak: mono ? 'break-all' : 'normal',
        lineHeight: 1.4,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const EdrDetailDrawerBase: FC<EdrDetailDrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  subtitleMono = false,
  headerIcon,
  topBadges,
  children,
  width = 420,
}) => {
  const classes = useDrawerStyles();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: 300, exit: 250 }}
      ModalProps={{
        BackdropProps: {
          className: classes.drawerBackdrop,
        },
      }}
      PaperProps={{
        className: classes.drawerPaper,
        style: {
          width,
          maxWidth: '92vw',
        },
      }}
    >
    <Box
      style={{
        height: '100%',
        overflowY: 'auto',
        background: EDR_COLORS.bgSurface,
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        style={{ gap: 10, padding: '14px 16px' }}
      >
        <Box minWidth={0}>
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 6, marginBottom: 4, flexWrap: 'wrap' }}
          >
            {headerIcon}
            <Typography
              component="div"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 15,
                fontWeight: 600,
                color: EDR_COLORS.textPrimary,
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
          </Box>
          {subtitle ? (
            <Typography
              style={{
                fontSize: 11,
                color: EDR_COLORS.textMuted,
                lineHeight: 1.4,
                fontFamily: subtitleMono
                  ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                  : 'Inter, sans-serif',
                wordBreak: subtitleMono ? 'break-all' : 'normal',
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          style={{
            border: `1px solid ${EDR_COLORS.borderDefault}`,
            borderRadius: 6,
            padding: 6,
          }}
        >
          <CloseIcon style={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {topBadges ? <Box style={{ padding: '0 16px 12px' }}>{topBadges}</Box> : null}

      {children}
      <Box style={{ height: 10 }} />
    </Box>
  </Drawer>
  );
};

export const EdrDetailDrawer = Object.assign(EdrDetailDrawerBase, {
  Section,
  Row: DetailRow,
});
